import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Button,
  Image,
  Alert,
  Spinner,
  Card,
} from "react-bootstrap";
import CollegeImagesService from "../../Services/CollegeImagesService";
import { toast } from "react-hot-toast";

function EditCollegeImage({
  showModal,
  handleCloseModal,
  collegeId,
  collegeName,
  onSuccess,
}) {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [newPreviewImages, setNewPreviewImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const fileInputRef = useRef(null);

  const fetchExistingImages = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await CollegeImagesService.getCollegeImages(collegeId);
      if (response.data.success) {
        setExistingImages(response.data.data);
        setIsLoading(false);
      } else {
        toast.error(response.data.message || "Failed to load existing images");
      }
    } catch (error) {
      console.error("Error fetching existing images:", error);
      setError("Failed to load existing images");
      setIsLoading(false);
    }
  }, [collegeId]);

  useEffect(() => {
    if (showModal && collegeId) {
      fetchExistingImages();
    }
  }, [showModal, collegeId, fetchExistingImages]);

  useEffect(() => {
    if (!showModal) {
      setSelectedFiles([]);
      setExistingImages([]);
      setImagesToDelete([]);
      setNewPreviewImages([]);
      setError("");
      setSuccess("");
    }
  }, [showModal]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setError("");

    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        setError("Only image files are allowed");
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        return false;
      }
      return true;
    });

    setSelectedFiles((prev) => [...prev, ...validFiles]);

    // Success-360 pattern: Create base64 previews
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setNewPreviewImages((prev) => [
          ...prev,
          {
            id: `new-${Date.now()}-${Math.random()}`,
            name: file.name,
            type: file.type,
            base64: base64String,
            size: file.size,
            isNew: true,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeExistingImage = (imageId) => {
    setImagesToDelete((prev) => [...prev, imageId]);
    setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
  };

  const removeNewImage = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setNewPreviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0 && existingImages.length === 0) {
      setError("Please keep or add at least one image");
      return;
    }

    setIsUploading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();

      // Append new files
      selectedFiles.forEach((file) => {
        formData.append("images", file);
      });

      // Append IDs of images to delete
      imagesToDelete.forEach((id) => {
        formData.append("deleteIds", id);
      });

      // Append college ID
      formData.append("collegeId", collegeId);

      const response = await CollegeImagesService.updateCollegeImages(
        formData,
        collegeId
      );

      if (response.data.success) {
        toast.success(response.data.message);
        handleCloseModal();
        if (onSuccess) {
          onSuccess();
        }
      } else {
        toast.error(response.data.message);
      }

      setIsUploading(false);
    } catch (error) {
      console.error("Error updating college images:", error);
      setError("Failed to update college images");
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    handleCloseModal();
  };

  return (
    <Modal show={showModal} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-image me-2"></i>
          Manage College Images - {collegeName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form>
          <Row className="mb-3">
            <Col>
              <Form.Label>Upload New Images</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileChange}
                ref={fileInputRef}
                disabled={isUploading}
              />
              <Form.Text className="text-muted">
                Select one or more images to upload (Max 5MB each)
              </Form.Text>
            </Col>
          </Row>

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <Row className="mt-4">
              <Col>
                <h6>Existing Images:</h6>
                <p className="text-muted">Click × to mark for deletion</p>
                <div className="d-flex flex-wrap gap-3">
                  {existingImages.map((image) => (
                    <div
                      key={image.id}
                      className="position-relative"
                      style={{ width: "150px", height: "150px" }}
                    >
                      <Image
                        src={image.imagePath}
                        alt={`College ${collegeName}`}
                        thumbnail
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 rounded-circle"
                        style={{ width: "30px", height: "30px", padding: 0 }}
                        onClick={() => removeExistingImage(image.id)}
                        disabled={isUploading}
                      >
                        &times;
                      </Button>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          {/* Loading state for existing images */}
          {isLoading && (
            <Row className="mt-3">
              <Col className="text-center">
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading images...</span>
                </Spinner>
                <p className="mt-2">Loading existing images...</p>
              </Col>
            </Row>
          )}

          {/* New Image Previews - Success-360 Pattern */}
          {newPreviewImages.length > 0 && (
            <Row className="mt-4">
              <Col>
                <h6>New Images to Upload:</h6>
                <div className="d-flex flex-wrap gap-3">
                  {newPreviewImages.map((preview, index) => (
                    <div
                      key={preview.id}
                      className="position-relative"
                      style={{ width: "150px", height: "150px" }}
                    >
                      <Image
                        src={`data:${preview.type};base64,${preview.base64}`}
                        alt={`Preview ${index + 1}`}
                        thumbnail
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="position-absolute top-0 end-0 rounded-circle"
                        style={{ width: "30px", height: "30px", padding: 0 }}
                        onClick={() => removeNewImage(index)}
                        disabled={isUploading}
                      >
                        &times;
                      </Button>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          {/* Summary Card */}
          {(existingImages.length > 0 ||
            newPreviewImages.length > 0 ||
            imagesToDelete.length > 0) && (
            <Row className="mt-4">
              <Col>
                <Card bg="light">
                  <Card.Body className="py-2">
                    <Row>
                      <Col md={4}>
                        <small>
                          Keeping: <strong>{existingImages.length}</strong>{" "}
                          images
                        </small>
                      </Col>
                      <Col md={4}>
                        <small>
                          Adding: <strong>{newPreviewImages.length}</strong> new
                          images
                        </small>
                      </Col>
                      <Col md={4}>
                        <small>
                          Removing: <strong>{imagesToDelete.length}</strong>{" "}
                          images
                        </small>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={isUploading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={
            isUploading ||
            (existingImages.length === 0 && newPreviewImages.length === 0)
          }
        >
          {isUploading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Updating Images...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditCollegeImage;
